import { DateInput } from '@amelio/ui-library';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ObjectiveReminderFields from './ObjectiveReminderFields';
import SectionHeading from '../SectionHeading';

import type { PeriodOption } from '@/hooks/objectives/usePeriodOptions';
import type { ObjectiveFormData } from '@/pages/Objectives/validation';
import type { DateInputProps } from '@amelio/ui-library';
import type { Control, FieldPath } from 'react-hook-form';

import { FieldErrorMessage } from '@/components/FieldErrorMessage/FieldErrorMessage';
import PeriodSelect from '@/components/objectives/PeriodSelect';
import { CUSTOM_DATES_OPTION_ID } from '@/constants/ObjectivePeriodLens';
import i18n from '@/plugins/i18n';
import { parseIsoDate, toDateInputLang, toIsoDate } from '@/utils/dateUtils';
import { formatLongDate } from '@/utils/objectivesFormat';

type DateFieldName = Extract<FieldPath<ObjectiveFormData>, 'startDate' | 'endDate'>;

interface DateFieldProps {
    name: DateFieldName;
    /** The other end of the range: its calendar bounds this one, and its order rule is re-checked. */
    otherEnd: DateFieldName;
    disabledDates: DateInputProps['disabledDates'];
    label: string;
    dataTestId: string;
}

interface ObjectivePeriodFieldsProps {
    control: Control<ObjectiveFormData>;
    options: PeriodOption[];
    selectedPeriodId: string;
    onPeriodChange: (periodId: string) => void;
    /** Already translated. */
    dateErrorMessages?: Partial<Record<DateFieldName, string>>;
    /** Forwarded to the picker so it waits rather than announcing "no objective period". */
    isLoadingPeriods?: boolean;
}

/**
 * The period picker and, below it, either a short read-only text or the start/end date pickers,
 * then the reminders counted back from the end date.
 * Once a period is selected it owns the dates (the server copies them over), so they are shown
 * as text; the editable date pickers appear when the picker is on "custom dates" -- an objective
 * kept on free dates, in either mode. The text reads straight from the form's own
 * `startDate`/`endDate`, so it always shows exactly what a submit would save.
 */
export default function ObjectivePeriodFields({
    control,
    options,
    selectedPeriodId,
    onPeriodChange,
    dateErrorMessages = {},
    isLoadingPeriods,
}: ObjectivePeriodFieldsProps) {
    const { t } = useTranslation();
    const lang = toDateInputLang(i18n.language);
    const selectedPeriod = options.find((option) => option.id === selectedPeriodId);
    const [startDate, endDate] = useWatch({ control, name: ['startDate', 'endDate'] });
    // Each calendar is bounded by the other date, so an inverted range cannot be picked from either.
    const startDay = parseIsoDate(startDate);
    const endDay = parseIsoDate(endDate);

    const renderDateField = ({
        name,
        otherEnd,
        disabledDates,
        label,
        dataTestId,
    }: DateFieldProps) => (
        <div
            role={'group'}
            aria-label={label}
            className={'flex flex-1 flex-col gap-1.5'}
        >
            <label className={'text-obj-muted text-[12px] font-medium tracking-[0.11px]'}>
                {label}
            </label>

            <Controller
                name={name}
                control={control}
                // The order rule is reported under both dates: changing one must re-check the
                // other, or its message would wait for the submit, or outlive the fix.
                rules={{ deps: otherEnd }}
                render={({ field }) => (
                    <DateInput
                        value={field.value}
                        onChange={(date) => date && field.onChange(toIsoDate(date))}
                        lang={lang}
                        placeholder={label}
                        ariaLabel={label}
                        disabledDates={disabledDates}
                        clearable={false}
                        // The form's cards carry a backdrop blur, which opens a stacking context:
                        // a calendar rendered in the flow is trapped inside its own card and the
                        // sections below it draw over it, whatever its z-index. Rendered in the
                        // portal, it escapes the trap — the same way
                        // the library's own Select, MultiSelect and Tooltip always do.
                        portalPanel
                        dataTestId={dataTestId}
                    />
                )}
            />

            <FieldErrorMessage message={dateErrorMessages[name]} />
        </div>
    );

    const startDateField = renderDateField({
        name: 'startDate',
        otherEnd: 'endDate',
        disabledDates: endDay ? { after: endDay } : undefined,
        label: t('objectives.form.startDate'),
        dataTestId: 'objective-form-start-date',
    });

    const endDateField = renderDateField({
        name: 'endDate',
        otherEnd: 'startDate',
        disabledDates: startDay ? { before: startDay } : undefined,
        label: t('objectives.form.endDate'),
        dataTestId: 'objective-form-end-date',
    });

    return (
        <div className={'flex flex-col gap-[48px]'}>
            <div className={'flex flex-col gap-3'}>
                <SectionHeading
                    icon={'essential-status'}
                    title={t('objectives.period.label')}
                />

                {/* An objective on free dates carries no period id at all; the picker names that
                    entry `CUSTOM_DATES_OPTION_ID`, so the two are translated here rather than
                    everywhere the form reads its own value. */}
                <PeriodSelect
                    options={options}
                    value={selectedPeriodId || CUSTOM_DATES_OPTION_ID}
                    onChange={(periodId) =>
                        onPeriodChange(periodId === CUSTOM_DATES_OPTION_ID ? '' : periodId)
                    }
                    allowCustomDates
                    allowAllPeriods={false}
                    isLoading={isLoadingPeriods}
                    surface={'field'}
                />

                {selectedPeriod ? (
                    <p
                        className={'text-obj-muted text-[14px]'}
                        data-testid={'objective-form-period-dates'}
                    >
                        {t('objectives.periodRange', {
                            start: formatLongDate(startDate, i18n.language),
                            end: formatLongDate(endDate, i18n.language),
                        })}
                    </p>
                ) : (
                    <div className={'obj-row-split'}>
                        {startDateField}
                        {endDateField}
                    </div>
                )}

                {/* A period owns its dates and shows no picker: its date error, if any, reads under it. */}
                {selectedPeriod && (
                    <FieldErrorMessage
                        message={dateErrorMessages.endDate ?? dateErrorMessages.startDate}
                    />
                )}
            </div>

            <ObjectiveReminderFields control={control} />
        </div>
    );
}
